import { MakeTag } from '../lib/jobMakes.tsx';
import { fmt, fmtDate, poNo, type CardPart } from '../api.ts';

export interface Liner {
  id?: number;
  sequenceNo?: number;
  linerSize: string | null;
  linerQty: string | number | null;
  gsm: string | number | null;
  bf: string | number | null;
  notes?: string | null;
  /** 085: a liner's paper (null reads as kraft) and a flute's profile. */
  material?: 'KRAFT' | 'WHITE_BACK' | null;
  flute?: string | null;
}

export interface Card {
  job_id: number;
  job_status: string;
  card_editable: boolean;
  card_made_at?: string | null;
  is_staged?: boolean;
  own_delivery_date?: boolean;
  line_delivery_date?: string | null;
  job_number: string;
  make?: string | null;
  job_date: string | null;
  ordered_quantity: string;
  production_quantity: string;
  plate_type: 'OLD' | 'NEW' | null;
  plate_set_number: string | null;
  plate_save: boolean;
  colour: string | null;
  printing_side: string | null;
  running_size: string | null;
  finishing_work: string | null;
  instructions: string | null;
  design_reference: string | null;
  board_description: string | null;
  board_sheet_size: string | null;
  board_sheet_count: string | null;
  ply: number | null;
  /** 085: printed on kraft, so layer 1 is a paper the corrugation card describes. */
  kraft_printing?: boolean;
  has_artwork: boolean;
  artwork_name: string | null;
  artwork_at: string | null;
  item_name: string;
  specification: string | null;
  box_size: string | null;
  po_number: string | null;
  po_date: string;
  delivery_date: string | null;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  customer_name: string;
  board_ready: boolean;
  board_lines: string;
  board_lines_available: string;
  liners: Liner[];
  route: { jobStageId: number; stageId: number; name: string;
           sequenceNo: number; status: string; alwaysIncluded: boolean }[];
  /* 058: read from one source rather than typed on the card. */
  box_type: string | null;
  ups: number | null;
  po_item_id: number;
  board_required: boolean;
  box_ply: number | null;
  boards: CardBoard[];
  waste_allowance_sheets: string | null;
  line_jobs?: number;
  ups_fixed_by?: string | null;
  card_revision: number;
  card_printed_at: string | null;
  changed_since_printed: boolean;
  /* 086: each sheet's revision on the floor (null until first printed after release),
     when it was printed, and whether the card has changed since. */
  printing_revision?: number | null;
  printing_printed_at?: string | null;
  printing_changed?: boolean;
  corrugation_revision?: number | null;
  corrugation_printed_at?: string | null;
  corrugation_changed?: boolean;
  print_count?: number;
  /** The route this box's last card used, offered to a draft that has none yet. */
  suggested_route: number[];
  suggested_route_from: string | null;
  added_stages: { jobStageId: number; reason: string | null }[];
  /* 067: the two cards -- which the route uses, ready, and the corrugation
     card still open after release until Corrugation starts. */
  has_printing: boolean;
  has_corrugation: boolean;
  /** Corrugation is on the route, so its card must be filled in; Flute
   *  Pasting alone (board bought corrugated) shows it but does not need it. */
  corrugation_required: boolean;
  printing_ready: boolean;
  corrugation_ready: boolean;
  corrugation_editable: boolean;
}

/** A board on the card, from Board stock. */
export interface CardBoard {
  itemId: number; name: string; sheets: number; lengthCm: number; widthCm: number;
}

/** "GREY BACK 230 GSM · 91.5 X 61 CM · 5,400 SHEETS", the way the paper card reads. */
export function boardText(b: CardBoard) {
  return `${b.name.split(' · ').slice(0, 2).join(' ')} · ${Number(b.lengthCm)} X ${Number(b.widthCm)} CM`
    + ` · ${fmt(String(b.sheets))} SHEETS`;
}

/** Sheets across every board on the card. */
export const totalSheets = (c: Card) => c.boards.reduce((t, b) => t + Number(b.sheets), 0);

export const PRIORITY_LABEL: Record<string, string> = {
  LOW: 'Low', NORMAL: 'Normal', HIGH: 'High', URGENT: 'Urgent',
};

const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

/** The Board / Paper line: every board on the card, from stock. */
function boardLine(c: Card) {
  if (c.boards.length) return c.boards.map(boardText).join('  +  ');
  // Cards written before board came from stock kept their own text.
  const parts = [
    c.board_description,
    c.board_sheet_size,
    c.board_sheet_count ? `${fmt(c.board_sheet_count)} SHEETS` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' - ') : '—';
}

/** What a print records (086): a draft's proof, or each sheet's revision, when and by whom. */
export interface PrintStamp {
  proof: boolean;
  sheets: { part: CardPart; revision: number | null; printedAt: string; printedBy: string | null }[];
}

/** "31 Oct 2026, 10:12" */
export const fmtStamp = (iso: string) => new Date(iso).toLocaleString('en-IN', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
});

/** Across the top of a sheet: a proof says so; a floor copy says its revision, when and by whom. */
function Stamp({ stamp, part }: { stamp: PrintStamp | null; part: CardPart }) {
  if (!stamp) return null;
  if (stamp.proof) return <div className="jc-draft">DRAFT · NOT FOR PRODUCTION</div>;
  const s = stamp.sheets.find((x) => x.part === part);
  if (!s?.revision) return null;
  return (
    <div className="jc-rev">
      <strong>REV {s.revision}</strong> · PRINTED {fmtStamp(s.printedAt).toUpperCase()}
      {s.printedBy ? ` · ${s.printedBy.toUpperCase()}` : ''}
    </div>
  );
}

function Strip({ c }: { c: Card }) {
  return (
    <div className="jc-strip">
      <span>{c.customer_name}</span>
      <span>{c.item_name}</span>
      {c.board_description && <span>{c.board_description}</span>}
      {c.board_sheet_size && <span>{c.board_sheet_size}</span>}
      {c.boards.length > 0 && <span>{fmt(String(totalSheets(c)))} SHEETS</span>}
      {c.running_size && <span>{c.running_size}</span>}
      <span>{fmt(c.production_quantity)} BOXES</span>
    </div>
  );
}

/** Boxes to run, and the sheets they take -- each with its unit, never one for the other. */
function TotalQty({ c }: { c: Card }) {
  const sheets = totalSheets(c);
  return (
    <span className="jc-val">
      {fmt(c.production_quantity)} BOXES{sheets > 0 ? ` · ${fmt(String(sheets))} SHEETS` : ''}
    </span>
  );
}

/** Blank boxes for the operator to fill in by hand, as they do today. */
function FloorBlock({ stage }: { stage: 'PRINTING' | 'CORRUGATION' }) {
  return (
    <table className="jc-floor">
      <thead>
        <tr>
          <th style={{ width: '18%' }}>Date</th>
          <th>{stage} starting time</th>
          <th>{stage} finished qty</th>
          <th>{stage} finishing time</th>
          <th>Operator sign</th>
        </tr>
      </thead>
      <tbody>
        <tr><td /><td /><td /><td /><td /></tr>
        <tr><td /><td /><td /><td /><td /></tr>
      </tbody>
    </table>
  );
}

/**
 * The corrugation card's layers as the floor reads them (085): one row per
 * layer of the ply, the board first -- or three blank rows when there is no
 * ply yet, to be written in by hand.
 */
function printedLayers(c: Card) {
  const ply = c.ply ?? c.box_ply;
  const kraft = Boolean(c.kraft_printing);
  const num = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? '' : fmt(v));
  const blank = { layer: '', paper: '', flute: '', size: '', qty: '', gsm: '', bf: '', notes: '' };
  if (!ply) {
    if (c.liners.length === 0) return [1, 2, 3].map((no) => ({ no, ...blank }));
    return c.liners.map((l, i) => ({ no: l.sequenceNo ?? i + 2, ...blank, size: dash(l.linerSize),
      qty: num(l.linerQty), gsm: num(l.gsm), bf: num(l.bf), notes: l.notes ?? '' }));
  }
  return Array.from({ length: ply }, (_, i) => i + 1).map((no) => {
    const b = c.boards[0];
    if (no === 1 && !kraft) {
      return { no, ...blank, layer: 'BOARD', paper: b ? b.name.toUpperCase() : '',
               size: b ? `${b.lengthCm} X ${b.widthCm} CM` : '', qty: b ? num(b.sheets) : '' };
    }
    const l = c.liners.find((x, i) => (x.sequenceNo ?? i + 2) === no);
    const role = no === 1 ? 'PRINTED KRAFT' : no % 2 === 0 ? 'FLUTE' : 'LINER';
    return {
      no, layer: role,
      paper: role === 'LINER' ? (l?.material === 'WHITE_BACK' ? 'WHITE BACK' : 'KRAFT') : 'KRAFT',
      flute: role === 'FLUTE' ? (l?.flute ?? '') : '',
      size: l?.linerSize ?? '', qty: num(l?.linerQty), gsm: num(l?.gsm), bf: num(l?.bf), notes: l?.notes ?? '',
    };
  });
}

/**
 * The printed OPAL JOB CARD: the printing card and the corrugation card, each
 * its own page, printed together or one at a time (067). Modelled on the
 * physical card so the floor recognises it.
 *
 * artworkSrc is passed in rather than fetched here, because the image sits
 * behind auth and the caller already holds the session.
 */
export default function JobCardPrint({
  card, artworkSrc, parts = ['PRINTING', 'CORRUGATION'], stamp = null,
}: { card: Card; artworkSrc?: string | undefined; parts?: CardPart[]; stamp?: PrintStamp | null }) {
  const c = card;

  return (
    <div className="print-only">
      {/* ------------------------------------------------ the printing card */}
      {parts.includes('PRINTING') && (
      <section className="jc-page">
        <Stamp stamp={stamp} part="PRINTING" />
        <Strip c={c} />
        <div className="jc-frame">
          <div className="jc-row">
            <div className="jc-cell" style={{ width: '30%' }}>
              <span className="jc-lab">Job no.</span>{' '}
              <span className="jc-box">{c.job_number}<MakeTag number={c.job_number} /></span>
            </div>
            <div className="jc-cell" style={{ flex: 1, padding: 0 }}>
              <div className="jc-title">OPAL JOB CARD</div>
            </div>
            <div className="jc-cell" style={{ width: '30%' }}>
              <span className="jc-lab">Date</span>{' '}
              <span className="jc-val">{fmtDate(c.job_date)}</span>
            </div>
          </div>

          {/* Priority sits at the top because it is the first thing the floor
              needs to know, and the printed card is what they hold. */}
          <div className="jc-row">
            <div className="jc-cell" style={{ width: '40%' }}>
              <span className="jc-lab">Customer PO</span>{' '}
              <span className="jc-val">{poNo(c.po_number)}</span>
            </div>
            <div className="jc-cell" style={{ width: '30%' }}>
              <span className="jc-lab">Delivery</span>{' '}
              <span className="jc-val">{fmtDate(c.delivery_date)}</span>
            </div>
            <div className="jc-cell" style={{ width: '30%' }}>
              <span className="jc-lab">Priority</span>{' '}
              {c.priority === 'NORMAL' || c.priority === 'LOW'
                ? <span className="jc-val">{PRIORITY_LABEL[c.priority]}</span>
                : <span className="jc-badge">{PRIORITY_LABEL[c.priority]}</span>}
            </div>
          </div>

          <div className="jc-row">
            <div className="jc-cell" style={{ width: '50%' }}>
              <span className="jc-lab">Plate</span>{' '}
              <span className="jc-box">{dash(c.plate_type)}</span>
            </div>
            <div className="jc-cell" style={{ width: '50%' }}>
              <span className="jc-lab">Plate set no.</span>{' '}
              <span className="jc-box">{dash(c.plate_set_number)}</span>
            </div>
          </div>

          <div className="jc-row">
            <div className="jc-cell" style={{ width: '50%' }}>
              <span className="jc-lab">Colour</span>{' '}
              <span className="jc-val">{dash(c.colour)}</span>
            </div>
            <div className="jc-cell" style={{ width: '50%' }}>
              <span className="jc-lab">Printing side</span>{' '}
              <span className="jc-val">{dash(c.printing_side)}</span>
            </div>
          </div>

          <div className="jc-row">
            <div className="jc-cell" style={{ flex: 1 }}>
              <span className="jc-lab">Item name</span>{' '}
              <span className="jc-val">{c.item_name}</span>
            </div>
          </div>

          <div className="jc-row">
            <div className="jc-cell" style={{ flex: 1 }}>
              <span className="jc-lab">Board / Paper</span>{' '}
              <span className="jc-val">{boardLine(c)}</span>
            </div>
          </div>

          <div className="jc-row">
            <div className="jc-cell" style={{ width: '50%' }}>
              <span className="jc-lab">Running size</span>{' '}
              <span className="jc-val">{dash(c.running_size)}</span>
            </div>
            <div className="jc-cell" style={{ width: '50%' }}>
              <span className="jc-badge">TOTAL QTY</span>{' '}
              <TotalQty c={c} />
            </div>
          </div>

          <div className="jc-row">
            <div className="jc-cell" style={{ flex: 1 }}>
              <span className="jc-lab">Finishing work</span>{' '}
              <span className="jc-val">{dash(c.finishing_work)}</span>
              {c.plate_save && (
                <span className="jc-badge" style={{ float: 'right' }}>PLATE SAVE</span>
              )}
            </div>
          </div>

          <div className="jc-row">
            <div className="jc-cell" style={{ flex: 1, minHeight: 42 }}>
              <span className="jc-lab">Any instruction</span>{' '}
              <span>{c.instructions ?? ''}</span>
            </div>
          </div>
        </div>

        <FloorBlock stage="PRINTING" />

        {artworkSrc && (
          <div className="jc-art">
            <img src={artworkSrc} alt="" />
          </div>
        )}
        {c.plate_set_number && <div className="jc-set">SET - {c.plate_set_number}</div>}
      </section>
      )}

      {/* --------------------------------------------- the corrugation card */}
      {parts.includes('CORRUGATION') && (
      <section className="jc-page">
        <Stamp stamp={stamp} part="CORRUGATION" />
        <Strip c={c} />
        <div className="jc-frame">
          <div className="jc-row">
            <div className="jc-cell" style={{ width: '30%' }}>
              <span className="jc-lab">Job no.</span>{' '}
              <span className="jc-box">{c.job_number}<MakeTag number={c.job_number} /></span>
            </div>
            <div className="jc-cell" style={{ flex: 1, padding: 0 }}>
              <div className="jc-title">OPAL JOB CARD — CORRUGATION</div>
            </div>
            <div className="jc-cell" style={{ width: '30%' }}>
              <span className="jc-lab">Date</span>{' '}
              <span className="jc-val">{fmtDate(c.job_date)}</span>
            </div>
          </div>

          <div className="jc-row">
            <div className="jc-cell" style={{ flex: 1 }}>
              <span className="jc-lab">Item name</span>{' '}
              <span className="jc-val">{c.item_name}</span>
            </div>
          </div>

          <div className="jc-row">
            <div className="jc-cell" style={{ width: '33%' }}>
              <span className="jc-lab">Ply</span>{' '}
              <span className="jc-box">{dash(c.ply ?? c.box_ply)}</span>
              {c.kraft_printing && (c.make === 'KRAFT_PRINT' || !c.make) && <>{' '}<span className="jc-badge">KRAFT PRINTING</span></>}
            </div>
            <div className="jc-cell" style={{ width: '33%' }}>
              <span className="jc-lab">Box size</span>{' '}
              <span className="jc-val">{dash(c.box_size)}</span>
            </div>
            <div className="jc-cell" style={{ flex: 1 }}>
              <span className="jc-badge">TOTAL QTY</span>{' '}
              <TotalQty c={c} />
            </div>
          </div>
        </div>

        <table className="jc-liners" style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th style={{ width: 34 }}>#</th>
              <th>Layer</th>
              <th>Paper</th>
              <th>Flute</th>
              <th>Size</th>
              <th>Qty</th>
              <th>GSM</th>
              <th>BF</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {printedLayers(c).map((l) => (
              <tr key={l.no}>
                <td>{l.no}</td>
                <td>{l.layer}</td>
                <td>{l.paper}</td>
                <td>{l.flute}</td>
                <td>{l.size}</td>
                <td>{l.qty}</td>
                <td>{l.gsm}</td>
                <td>{l.bf}</td>
                <td>{l.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="jc-frame" style={{ marginTop: 10 }}>
          <div className="jc-row">
            <div className="jc-cell" style={{ flex: 1, minHeight: 42 }}>
              <span className="jc-lab">Any instruction</span>{' '}
              <span>{c.instructions ?? ''}</span>
            </div>
          </div>
        </div>

        <FloorBlock stage="CORRUGATION" />
      </section>
      )}
    </div>
  );
}
